import { SupportSettings } from "@/pages/SupportPage";
import { EquipmentSettings } from "@/components/site-equipment/EquipmentSettings";
import { CountrySettingsPanel } from "@/components/radnici/CountrySettingsPanel";
import { EquipmentTypesSettings } from "@/components/oprema/EquipmentTypesSettings";
import { ProcurementSettings, SupplierSettings } from "@/components/nabava/ProcurementSettings";
import { ServiceSettings } from "@/components/nabava/ServiceSettings";
import { ConstructionSettings } from "@/components/nabava/ConstructionSettings";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, PageHeader } from "@/components/ui/design-system";
import { porukaDohvata } from "@/lib/greske";
import { spremiPostavke, ucinakUkljucenja, usePostavke, type UcinakUkljucenja } from "@/lib/postavke";

export function PostavkePage() {
  const { user, can } = useAuth();
  const superadmin = user?.roles?.some(role => role.code === "superadmin");
  const countryAllowed = user?.permissions.some(p => p.module === "radnik" && p.action === "update" && p.scope === "all");
  const policyAllowed = user?.permissions.some(p => p.module === "auth" && p.action === "update" && p.scope === "all");
  const equipmentAllowed = can("sifrarnik", "update");
  if (!countryAllowed && !policyAllowed && !equipmentAllowed && !superadmin) return <Navigate to="/" replace />;
  return <div className="space-y-6">{superadmin && <SupportSettings />}{countryAllowed && <CountrySettingsPanel />}{equipmentAllowed && <><EquipmentTypesSettings /><SupplierSettings /><ConstructionSettings /><ServiceSettings /></>}{policyAllowed && <><OperationalSettings /><ProcurementSettings /><EquipmentSettings /></>}</div>;
}
function OperationalSettings() {
  const { user } = useAuth();
  const { postavke, error, loading } = usePostavke();
  const [target, setTarget] = useState<boolean | null>(null);
  const [reason, setReason] = useState("");
  const [impact, setImpact] = useState<UcinakUkljucenja | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const allowed = user?.permissions.some((p) => p.module === "auth" && p.action === "update" && p.scope === "all");
  if (!allowed) return <Navigate to="/" replace />;
  const enabled = target ?? postavke?.kontrola_osposobljenosti ?? true;
  async function persist() {
    setBusy(true); setFailure(null);
    try {
      await spremiPostavke({ kontrola_osposobljenosti: enabled, razlog: reason.trim() });
      setImpact(null); setReason(""); setSaved(true);
    } catch (e: unknown) { setFailure(porukaDohvata(e)); }
    finally { setBusy(false); }
  }
  async function submit() {
    if (!reason.trim() || !postavke || busy) return;
    if (!enabled) { await persist(); return; }
    setBusy(true); setFailure(null);
    try { setImpact(await ucinakUkljucenja()); }
    catch (e: unknown) { setFailure(porukaDohvata(e)); }
    finally { setBusy(false); }
  }
  return <div className="flex flex-col gap-5">
    <PageHeader title="Postavke" meta="Kontrola osposobljenosti" />
    {(error || failure) && <p role="alert">{failure || error}</p>}
    {loading ? <p role="status">Učitavanje postavki…</p> : postavke && <form className="flex max-w-2xl flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <label className="flex min-h-11 items-center gap-3"><input type="checkbox" role="switch" className="size-6 shrink-0" checked={enabled} disabled={busy} onChange={(e) => { setTarget(e.target.checked); setSaved(false); }} />Kontrola osposobljenosti pri raspoređivanju i unosu sati</label>
      <p>Uključeno: osposobljenost određuje efektivan raspored i mogućnost unosa sati. Nepodobni rasporedi čekaju odobrenje iznimke.</p>
      <p>Isključeno: traje prijelazno razdoblje. Upozorenja ostaju vidljiva, a raspoređivanje i unos sati prolaze bez blokade osposobljenosti.</p>
      <label className="flex flex-col gap-2">Razlog promjene (obavezno)<textarea className="min-h-24 rounded-lg border p-3" required maxLength={2000} value={reason} disabled={busy} onChange={(e) => { setReason(e.target.value); setSaved(false); }} /></label>
      <Button className="min-h-11 self-start" disabled={busy || !reason.trim() || enabled === postavke.kontrola_osposobljenosti} type="submit">{busy ? "Spremanje…" : "Spremi postavke"}</Button>
    </form>}
    {saved && <p role="status">Postavke su spremljene.</p>}
    {impact && <Dialog title="Uključiti kontrolu osposobljenosti?" onClose={() => { if (!busy) setImpact(null); }} actions={<><Button variant="outline" className="min-h-11" disabled={busy} onClick={() => setImpact(null)}>Odustani</Button><Button className="min-h-11" disabled={busy} onClick={() => void persist()}>Potvrdi uključivanje</Button></>}>
      <p>Aktivni rasporedi koji prelaze na čekanje odobrenja iznimke: <strong>{impact.aktivni_rasporedi}</strong>.</p>
      <p>Radnici koji danas ne bi mogli dobiti sate: <strong>{impact.radnici_bez_sati}</strong>.</p>
      <p>Brojevi vrijede u trenutku pregleda. Postojeće odluke o iznimkama ostaju sačuvane.</p>
    </Dialog>}
  </div>;
}
